/**
 * AI search for the header search modal (snippets/search-modal.liquid).
 *
 * The modal renders Shopify's native predictive-search component. When AI
 * search is enabled we take over the input inside the modal: type -> query the
 * AI API for matching product handles -> re-render the SAME predictive-search
 * section with those handles (q="handle:a OR handle:b …"). Because the markup
 * comes straight from sections/predictive-search.liquid, the results look and
 * behave exactly like the native list the modal already shows — the theme's
 * existing custom CSS styles them unchanged.
 *
 * When AI search is DISABLED the script does nothing and the native predictive
 * search runs untouched. When AI search is ENABLED there is no native fallback:
 * an API/section error shows the AI error message and an empty result set shows
 * the AI "no results" message.
 *
 * Config comes from data-* attributes on [data-ai-search-modal]:
 *   data-ai-enabled      "true" to use the AI API
 *   data-api-url         AI search API origin (settings.ai_search_api_url)
 *   data-shop            shop.permanent_domain
 *   data-locale          request locale
 *   data-limit           number of products to request (default 6)
 *   data-section-id      predictive-search section id to render (default "predictive-search")
 *   data-page-url        AI results page path (settings.ai_search_page_url, locale-prefixed)
 *   data-i18n-no-results no-results text ("%query%" placeholder)
 *   data-i18n-error      error text
 *
 * Submitting (Enter or a search button) navigates to the AI results page
 * (data-page-url), exactly like the homepage AI bar.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 400;
  var MIN_CHARS = 2;

  // #143762: one dataLayer event per search a shopper completes, for the GA4
  // search terms; Google Tag Manager (GTM-WMC4VJV) sends it on to GA4.
  function pushSearchEvent(term, source) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'ki_search', search_term: term, search_source: source });
  }

  function initModal(root) {
    if (root.hasAttribute('data-ai-search-modal-ready')) return;
    root.setAttribute('data-ai-search-modal-ready', '');

    var aiEnabled = (root.getAttribute('data-ai-enabled') || 'false') === 'true';
    var apiUrl = root.getAttribute('data-api-url') || '';
    var shop = root.getAttribute('data-shop') || '';
    var locale = root.getAttribute('data-locale') || 'de';
    var limit = parseInt(root.getAttribute('data-limit') || '6', 10);
    var sectionId = root.getAttribute('data-section-id') || 'predictive-search';
    var pageUrl = root.getAttribute('data-page-url') || '/search';
    var i18n = {
      noResults: root.getAttribute('data-i18n-no-results') || 'Keine Ergebnisse für „%query%“',
      error: root.getAttribute('data-i18n-error') || 'Die Suche ist momentan nicht verfügbar.'
    };

    // Nothing to do without a working AI config — leave the native search alone.
    if (!aiEnabled || !apiUrl || !shop) return;

    var component = root.querySelector('predictive-search-component');
    var input = root.querySelector('.search-input, [ref="searchInput"]');
    var results = root.querySelector('[ref="predictiveSearchResults"], .predictive-search-form__content');
    var form = root.querySelector('form.predictive-search-form, form');
    if (!component || !input || !results) return;

    // Point the native GET form at the AI results page so a plain submit lands
    // there, and navigate there ourselves on Enter (before the native keydown
    // handler can send the user to /search).
    if (form) form.setAttribute('action', pageUrl);

    function aiPageSearchUrl(query) {
      return pageUrl + (pageUrl.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(query);
    }

    function goToAiPage(query) {
      if (!query) return;
      window.location.href = aiPageSearchUrl(query);
    }

    // Send a bare Enter to the AI results page instead of /search.
    // predictive-search.js handles keydown from document in the capture phase,
    // so its Enter runs first; our later navigation replaces its /search one.
    input.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'Enter') return;
        var query = input.value.trim();
        if (!query) return;

        // If the user has keyboard-selected a result item, native Enter has
        // already opened that product. Only a "bare" Enter (nothing
        // highlighted) should go to the AI results page.
        var selected = results.querySelector('[aria-selected="true"]');
        if (selected) {
          // Opening a suggestion completes the search. With a single result
          // native navigates without a click, so report it here too.
          reportSuggestion(selected);
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        goToAiPage(query);
      },
      true
    );

    if (form) {
      form.addEventListener('submit', function (event) {
        var query = input.value.trim();
        if (!query) return;
        event.preventDefault();
        goToAiPage(query);
      });
    }

    var debounceTimer = null;
    var aiController = null;
    var sectionController = null;
    var lastRenderedQuery = null;

    // Capture-phase listener runs before the component's own on:input handler.
    // When we handle the query with AI we stopImmediatePropagation so the native
    // predictive search never fires for the same keystroke. On any AI failure we
    // simply let the event through and native search takes over.
    input.addEventListener(
      'input',
      function (event) {
        var query = input.value.trim();

        if (query.length < MIN_CHARS) {
          // Let native handle clearing / short queries (it resets the panel).
          clearTimeout(debounceTimer);
          abort();
          lastRenderedQuery = null;
          return;
        }

        // Take ownership of this keystroke so native search stays quiet.
        event.stopImmediatePropagation();

        clearTimeout(debounceTimer);
        var currentQuery = query;
        debounceTimer = setTimeout(function () {
          runAiSearch(currentQuery);
        }, DEBOUNCE_MS);
      },
      true
    );

    function abort() {
      if (aiController) aiController.abort();
      if (sectionController) sectionController.abort();
      aiController = null;
      sectionController = null;
    }

    function runAiSearch(query) {
      abort();
      aiController = new AbortController();

      var url = apiUrl + '/api/ai-search' +
        '?q=' + encodeURIComponent(query) +
        '&shop=' + encodeURIComponent(shop) +
        '&locale=' + encodeURIComponent(locale) +
        '&limit=' + limit +
        // An off-topic query gets the closest products instead of "Keine
        // Ergebnisse" — the same answer the results page gives (#15613, A).
        '&fallback=1&understand=1';

      fetch(url, { signal: aiController.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var handles = ((data && data.products) || [])
            .map(function (p) { return p && p.handle; })
            .filter(Boolean);

          if (!handles.length) {
            renderNoResults(query);
            return;
          }
          renderHandles(handles, query);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // AI is the only search here — show the AI error state, never native.
          renderError();
        });
    }

    // Render the AI-selected products through the predictive-search section so
    // the markup (and therefore the styling) is identical to native results.
    function renderHandles(handles, query) {
      if (sectionController) sectionController.abort();
      sectionController = new AbortController();

      var handleQuery = handles
        .map(function (h) { return 'handle:' + h; })
        .join(' OR ');

      var sectionUrl = new URL(Theme.routes.predictive_search_url, location.origin);
      sectionUrl.searchParams.set('q', handleQuery);
      sectionUrl.searchParams.set('section_id', sectionId);
      sectionUrl.searchParams.set('resources[type]', 'product');
      sectionUrl.searchParams.set('resources[limit]', String(handles.length));

      fetch(sectionUrl.toString(), { signal: sectionController.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (markup) {
          var parsed = new DOMParser()
            .parseFromString(markup, 'text/html')
            .querySelector('.predictive-search-dropdown');

          if (!parsed || !parsed.querySelector('.predictive-search-results__products')) {
            // Section returned nothing usable (e.g. handles unpublished) —
            // present it as "no results" rather than falling back to native.
            renderNoResults(query);
            return;
          }

          reorderToAiOrder(parsed, handles);
          // The search event reads its term from here (#143762).
          parsed.setAttribute('data-ai-query', query);
          results.replaceChildren(parsed);
          lastRenderedQuery = query;
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          renderError();
        });
    }

    // The section returns products in Shopify's order, not AI relevance order.
    // Re-sort the rendered cards to match the AI handle order.
    function reorderToAiOrder(dropdown, handles) {
      var list = dropdown.querySelector('.predictive-search-results__wrapper-products');
      if (!list) return;

      var order = {};
      handles.forEach(function (h, i) { order[h] = i; });

      var cards = Array.prototype.slice.call(
        list.querySelectorAll('.predictive-search-results__card')
      );

      cards
        .map(function (card) {
          var link = card.querySelector('a[href*="/products/"]');
          var handle = '';
          if (link) {
            var m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
            if (m) handle = m[1];
          }
          return { card: card, rank: handle in order ? order[handle] : Number.MAX_SAFE_INTEGER };
        })
        .sort(function (a, b) { return a.rank - b.rank; })
        .forEach(function (item) { list.appendChild(item.card); });
    }

    function renderNoResults(query) {
      var wrapper = document.createElement('div');
      wrapper.className = 'predictive-search-dropdown';
      wrapper.setAttribute('role', 'listbox');
      var inner = document.createElement('div');
      inner.className = 'predictive-search-results__inner';
      inner.setAttribute('data-search-results', '');
      var msg = document.createElement('p');
      msg.className = 'predictive-search-results__no-results';
      msg.textContent = i18n.noResults.replace('%query%', query);
      inner.appendChild(msg);
      wrapper.appendChild(inner);
      results.replaceChildren(wrapper);
      lastRenderedQuery = query;
    }

    function renderError() {
      var wrapper = document.createElement('div');
      wrapper.className = 'predictive-search-dropdown';
      wrapper.setAttribute('role', 'listbox');
      var inner = document.createElement('div');
      inner.className = 'predictive-search-results__inner';
      inner.setAttribute('data-search-results', '');
      var msg = document.createElement('p');
      msg.className = 'predictive-search-results__no-results';
      msg.textContent = i18n.error;
      inner.appendChild(msg);
      wrapper.appendChild(inner);
      results.replaceChildren(wrapper);
      lastRenderedQuery = null;
    }

    // Opening a product from the AI results completes the search: report it
    // once per query, whether by click or by Enter on a selected result. The
    // term comes from the AI render the item sits in, so other markup in the
    // panel never counts. A bare Enter and the search button open the
    // KI-Suche page, which reports it there.
    var reportedQuery = null;
    function reportSuggestion(item) {
      var render = item.closest('[data-ai-query]');
      var query = render && render.getAttribute('data-ai-query');
      if (!query || query === reportedQuery) return;
      reportedQuery = query;
      pushSearchEvent(query, 'dropdown');
    }
    results.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('.predictive-search-results__card a[href*="/products/"]');
      if (link) reportSuggestion(link);
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-ai-search-modal]').forEach(initModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-init after theme editor section reloads.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  window.AISearchModal = { initAll: initAll };
})();
